import React from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AIGenerateButton } from "./AIGenerateButton";
import { AccountType } from "../../../models/AccountData";
import { OptionalRender } from "../../Common/OptionalRender";
import { HookFormError } from "../Error";

import "./styles.scss";

interface IHookFormTextareaProps extends React.ComponentProps<"textarea"> {
  name: string;
  label?: string;
  customizedPromptName?: string;
  maxChars?: number;
  showChars?: boolean;
  aiGenerateUrl?: string;
}

export const HookFormTextarea = (props: IHookFormTextareaProps) => {
  const {
    name,
    label,
    customizedPromptName,
    className,
    showChars,
    maxChars,
    aiGenerateUrl,
    ...rest
  } = props;

  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityDetailsPage.modalEditProfile.editProfileForm.${key}`);
  const { register, formState, watch, setValue } = useFormContext();
  const value = watch(name);
  const customizedPrompt = watch(customizedPromptName ?? "");

  const handleAiGenerate = (text: string) => {
    setValue(name, text, { shouldDirty: true });
  };

  return (
    <div className={`hook-form-textarea ${className || ""}`}>
      {label && (
        <label className="hook-form-textarea-label" htmlFor={name}>
          {label}
        </label>
      )}
      <div className="hook-form-textarea-container">
        <textarea {...register(name)} {...rest} />
        <OptionalRender
          mustBeImpersonating={true}
          mustBeOneOfRoles={[AccountType.ADMIN]}
          mustBeTrue={!!customizedPromptName}
        >
          <div className="hook-form-textarea-divider" />
          <textarea
            className="hook-form-textarea-customized-prompt"
            {...register(customizedPromptName ?? "")}
            placeholder={t("customized_prompt")}
          />
        </OptionalRender>
        {aiGenerateUrl && (
          <AIGenerateButton
            url={aiGenerateUrl}
            onGenerate={handleAiGenerate}
            customizedPrompt={customizedPrompt}
          />
        )}
      </div>
      <div className="hook-form-status-display">
        <HookFormError error={formState.errors[name]?.message?.toString()} />
        {showChars && (
          <span
            className={`hook-form-status-display-chars ${
              maxChars && value.length > maxChars ? "over" : ""
            }`}
          >
            {`${value.length} ${maxChars ? `/ ${maxChars}` : ""}`}
          </span>
        )}
      </div>
    </div>
  );
};
